import React, { useState, useEffect } from 'react'
import { useMutation } from '@apollo/client'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from 'app/rootReducer'
import useLoadingDelay from 'hooks/useLoadingDelay'
import { loadingSpinnerPortal } from 'components/atoms/LoadingSpinner'
import { setEmailAddress } from 'reducers/authentication/authenticationSlice'
import { setEmailAddress as setUserProfileEmailAddress } from 'reducers/userprofile/userprofileSlice'
import './styles.scss'
import {
  VERIFY_UPDATED_EMAIL_ADDRESS,
  VerifyUpdatedEmailAddressResponse,
  VerifyUpdatedEmailAddressResponseData,
  VerifyUpdatedEmailAddressVariables,
  RESEND_UPDATED_EMAIL_ADDRESS_CODE,
  ResendUpdatedEmailAddressCodeResponse,
  ResendUpdatedEmailAddressCodeResponseData,
  CANCEL_UPDATED_EMAIL_ADDRESS,
  CancelUpdatedEmailAddressResponse,
  CancelUpdatedEmailAddressResponseData
} from 'share/mutations/account'
import GoBackHeader from 'components/organisms/GoBackHeader'
import UserProfileVerifyEmailAddressForm from 'components/organisms/UserProfileVerifyEmailAddressForm'
import { AuthVerifyContainer } from 'components/atoms/Layout'
import ErrorMessage from 'components/atoms/ErrorMessage'
import { obfuscateEmailAddress } from 'share/utils/utils'

interface Props {
  nextPage(page: string): void
  previousPage(page: string): void
}

const UserProfileVerifyEmailAddress = (props: Props): JSX.Element => {
  const { nextPage } = props
  const userprofile = useSelector((state: RootState) => state.userprofile)
  const dispatch = useDispatch()
  const [obfuscatedEmailAddress, setObfuscatedEmailAddress] = useState('')
  const [enteredCodeComplete, setEnteredCodeComplete] = useState(false)
  const [enteredCode, setEnteredCode] = useState('')
  const [errorMessage, setErrorMessage] =
    useState<string | null | undefined>(null)
  const authentication = useSelector((state: RootState) => state.authentication)
  const [verifyUpdatedEmailAddress, { loading }] = useMutation<
    VerifyUpdatedEmailAddressResponse,
    VerifyUpdatedEmailAddressVariables
  >(VERIFY_UPDATED_EMAIL_ADDRESS)
  const [resendUpdatedEmailAddressCode, { loading: loadingEmailAddressCode }] =
    useMutation<ResendUpdatedEmailAddressCodeResponse>(
      RESEND_UPDATED_EMAIL_ADDRESS_CODE
    )
  const [cancelUpdatedEmailAddress, { loading: loadingCancelEmailAddress }] =
    useMutation<CancelUpdatedEmailAddressResponse>(CANCEL_UPDATED_EMAIL_ADDRESS)
  const loadingDelay = useLoadingDelay(
    loading || loadingEmailAddressCode || loadingCancelEmailAddress
  )

  function setObfuscatedEmailAddressHelper(): void {
    let obfuscatedEmailAddressValue = ''
    if (authentication) {
      if (authentication.account) {
        if (authentication.account.emailAddress) {
          const emailAddress = authentication.account.emailAddress
          obfuscatedEmailAddressValue = obfuscateEmailAddress(emailAddress)
        }
      }
    }
    setObfuscatedEmailAddress(obfuscatedEmailAddressValue)
  }

  useEffect(() => {
    setObfuscatedEmailAddressHelper()
  }, [authentication])

  async function verifyUpdatedEmailAddressHelper(
    input: VerifyUpdatedEmailAddressVariables
  ): Promise<VerifyUpdatedEmailAddressResponseData> {
    const result: VerifyUpdatedEmailAddressResponseData = {
      success: false,
      errorMessage: null
    }

    setErrorMessage(null)
    const errMsg = 'Verifying your EmailAddress had an error. Please try again.'
    try {
      const verifyUpdatedEmailAddressResult = await verifyUpdatedEmailAddress({
        variables: input
      })
      if (
        verifyUpdatedEmailAddressResult.data?.accounts.verifyUpdatedEmailAddress
          .success
      ) {
        result.success = true
        nextPage('edit')
      } else {
        result.success = false
        result.errorMessage =
          verifyUpdatedEmailAddressResult.data?.accounts.verifyUpdatedEmailAddress.errorMessage
        setErrorMessage(result.errorMessage || errMsg)
      }
    } catch (e) {
      result.success = false
      result.errorMessage = errMsg
      setErrorMessage(result.errorMessage)
    }
    return result
  }

  async function resendUpdatedEmailAddressCodeHelper(): Promise<ResendUpdatedEmailAddressCodeResponseData> {
    const result: ResendUpdatedEmailAddressCodeResponseData = {
      success: false
    }

    setErrorMessage(null)
    const errMsg =
      'Error resending verification code for email address. Please try again later.'
    try {
      setEnteredCode('')
      const resendUpdatedEmailAddressCodeResult =
        await resendUpdatedEmailAddressCode()
      if (
        resendUpdatedEmailAddressCodeResult.data?.accounts
          .resendUpdatedEmailAddressCode.success
      ) {
        result.success = true
      } else {
        result.success = false

        setErrorMessage(errMsg)
      }
    } catch (e) {
      result.success = false
      setErrorMessage(errMsg)
    }
    return result
  }

  async function cancelUpdatedEmailAddressHelper(): Promise<CancelUpdatedEmailAddressResponseData> {
    const result: CancelUpdatedEmailAddressResponseData = {
      success: false,
      errorMessage: null,
      emailAddress: null
    }

    setErrorMessage(null)
    const errMsg =
      'Canceling your Email Address had an error. Please try again.'
    try {
      const cancelUpdatedEmailAddressResult = await cancelUpdatedEmailAddress()
      if (
        cancelUpdatedEmailAddressResult.data?.accounts
          .cancelUpdateAccountEmailAddress.success
      ) {
        result.success = true
        result.emailAddress =
          cancelUpdatedEmailAddressResult.data?.accounts.cancelUpdateAccountEmailAddress.emailAddress
        if (result.emailAddress) {
          dispatch(setEmailAddress(result.emailAddress))
          if (userprofile) {
            if (userprofile.emailAddress) {
              dispatch(setUserProfileEmailAddress(result.emailAddress))
            }
          }
        }

        nextPage('edit')
      } else {
        result.success = false
        result.errorMessage =
          cancelUpdatedEmailAddressResult.data?.accounts.cancelUpdateAccountEmailAddress.errorMessage
        setErrorMessage(result.errorMessage || errMsg)
      }
    } catch (e) {
      result.success = false
      result.errorMessage = errMsg
      setErrorMessage(result.errorMessage || errMsg)
    }
    return result
  }

  const renderFailViolator = () => {
    return (
      <ErrorMessage>
        <span>{errorMessage}</span>
      </ErrorMessage>
    )
  }

  return (
    <React.Fragment>
      <GoBackHeader
        text='Change Email Address'
        onClick={() => props.previousPage('edit')}
      />
      <AuthVerifyContainer data-testid='user-profile-verify-email-address-page'>
        {loadingDelay && loadingSpinnerPortal()}
        {errorMessage && renderFailViolator()}
        <h1 data-testid='user-profile-verify-email-address-header'>
          Verify your email address.
        </h1>
        <span
          className='verify-message-text'
          data-testid='user-profile-verify-email-address-message'
        >
          A verification code has been sent to to your email address,
          <strong data-testid='user-profile-verify-email-address-obfuscated'>
            {' '}
            {obfuscatedEmailAddress}.
          </strong>
        </span>
        <UserProfileVerifyEmailAddressForm
          verifyUpdatedEmailAddressHelper={verifyUpdatedEmailAddressHelper}
          enteredCode={enteredCode}
          enteredCodeComplete={enteredCodeComplete}
          resendUpdatedEmailAddressCodeHelper={
            resendUpdatedEmailAddressCodeHelper
          }
          setEnteredCode={setEnteredCode}
          setEnteredCodeComplete={setEnteredCodeComplete}
          cancelUpdatedEmailAddressHelper={cancelUpdatedEmailAddressHelper}
        />
      </AuthVerifyContainer>
    </React.Fragment>
  )
}

export default UserProfileVerifyEmailAddress
